import { useLocaleStore } from './store';
import type { Locale } from './locales/_index';

const TAG: Record<Locale, string> = { en: 'en-US', zh: 'zh-CN', 'zh-tw': 'zh-TW', ja: 'ja-JP', ko: 'ko-KR', vi: 'vi-VN' };

/**
 * Parse a multi-locale string of the form `"en: foo | zh: bar | zh-tw: baz"`.
 * Returns the value for the requested locale (with base-language and `en`
 * fallbacks), or `null` when the string is not in this format so the caller
 * can apply its own fallback logic.
 */
export function pickLocaleString(value: string, locale: Locale): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  // Must open with a locale tag followed by a colon and non-whitespace content,
  // e.g. "en: Name" or "zh-tw: 名稱". Plain strings without a tag return null.
  if (!/^[a-z]{2}(-[a-z]{2,4})?:\s*\S/i.test(trimmed)) return null;
  const map: Record<string, string> = {};
  for (const part of trimmed.split('|')) {
    const p = part.trim();
    if (!p) continue;
    const colonIdx = p.indexOf(':');
    if (colonIdx <= 0) continue;
    const key = p.slice(0, colonIdx).trim().toLowerCase();
    const val = p.slice(colonIdx + 1).trim();
    if (val) map[key] = val;
  }
  if (Object.keys(map).length === 0) return null;
  const lang = locale.toLowerCase().split('-')[0];
  return map[locale.toLowerCase()] ?? map[lang] ?? map['en'] ?? Object.values(map)[0] ?? null;
}

/**
 * Resolve a plain user-facing text field that may be locale-tagged.
 * Non-tagged strings pass through unchanged.
 */
export function resolveLocaleText(value: string | undefined, locale: Locale): string {
  if (!value) return '';
  return pickLocaleString(value, locale) ?? value;
}

// Backend exposes a parallel `name_zh` (or `game_name_zh`) column on game-
// bearing endpoints. The new API also ships names as a multi-locale string
// `"en: Name | zh: 名称"` in the `name` field. Both formats are handled here
// so every consumer gets the right locale automatically.
export function pickLocaleName(
  name: string | undefined,
  nameZh: string | undefined,
  locale: Locale,
): string {
  if (name) {
    const parsed = pickLocaleString(name, locale);
    if (parsed !== null) return parsed;
  }
  // Legacy two-column format: `name` (en) + `name_zh` (zh).
  if (locale === 'zh' && nameZh) return nameZh;
  return name ?? '';
}

const useLocaleTag = () => TAG[useLocaleStore((s) => s.locale)];

export const useFormatNumber = () => {
  const tag = useLocaleTag();
  return (n: number, opts?: Intl.NumberFormatOptions) =>
    new Intl.NumberFormat(tag, opts).format(n);
};

export const useFormatDate = () => {
  const tag = useLocaleTag();
  return (d: Date | number, opts?: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(tag, opts).format(d);
};

export const useFormatMoney = () => {
  const fmt = useFormatNumber();
  return (n: number, currency = 'USDT') =>
    `${fmt(n, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
};
